import Image from 'next/image';
import IconGeo from '@app/assets/img/geo.svg';
import ImgStub from '../img/stub.png';
import { Button, MyLink, Phone, SkeletonLoading } from '@app/shared';
import { useAppDispatch, useAppSelector } from '@app/redux/hooks';
import { Fragment, useEffect } from 'react';
import { WrapperList } from '@app/shared/ui/WrapperList';
import { getRecordService, getIsLoadedUserRecord } from '../../module/selectors/getRecordService';
import { declensionFactory } from '@app/helpers/declension';
import { TitleList } from '@app/shared/ui/WrapperList/TitleList';
import { EmptyListStub } from '@app/shared/ui/EmptyListStub';
import { fetchGetRecordsTireFitting } from '@entities/Services';
import { IRecordServiceItemProps } from '../../module/types/TypeRecord';
import { useWindowSizing } from '@app/hooks/useSizeWindow';
import { BuildRoute } from '@entities/Shop';

import styles from './RecordsService.module.scss';

const declineRecord = declensionFactory('запись', 'записи', 'записей');

export const RecordService = ({ title, titleMob }) => {
  const isMobile = useWindowSizing(990);
  const dispatch = useAppDispatch();
  const records = useAppSelector(getRecordService);
  const isLoaded = useAppSelector(getIsLoadedUserRecord);

  useEffect(() => {
    if (!isLoaded && !records.length) {
      dispatch(fetchGetRecordsTireFitting());
    }
  }, []);

  if (!isLoaded) {
    return <RecordService.Skeleton />;
  }

  if (isLoaded && !records.length) {
    return (
      <Fragment>
        <TitleList title={title} />
        <EmptyListStub
          title="Пока нет записей на услуги в Колесо.ру"
          textBtn="Записаться сейчас"
          href="/rec/?widgetOpened"
          img={<Image src={ImgStub} alt={'Записаться сейчас'} />}
        />
      </Fragment>
    );
  }

  return (
    <Fragment>
      <TitleList title={isMobile ? titleMob : title} text={records.length + ' ' + declineRecord(records.length)} />
      <div className={styles.Wrapper}>
        {records.map(record => (
          <RecordService.RecordServiceItem {...record} key={record.id} recordsCount={records.length} />
        ))}
      </div>
    </Fragment>
  );
};

RecordService.RecordServiceItem = function RecordServiceItem({
  gtd,
  carName,
  shopName,
  shopId,
  date,
  code,
  subDomain,
  ox,
  shopPhone,
  service,
  recordsCount,
}: IRecordServiceItemProps) {
  return (
    <WrapperList
      className={styles.WrapperList}
      title={recordsCount + ' ' + declineRecord(recordsCount)}
      text={`${date.textTime + ' в ' + date.time}`}
      service={service}
    >
      <div className={styles.Container}>
        <div className={styles.Content}>
          <div className={styles.Address}>
            <IconGeo width={25} height={25} />
            <span>{shopName}</span>
          </div>
          <BuildRoute shopId={shopId} />
        </div>
        <div className={styles.Info}>
          <div className={styles.Block}>
            {gtd && (
              <div className={styles.Column}>
                <div>Гос. номер:</div>
                <div className={styles.Text}>{gtd}</div>
              </div>
            )}
            {carName && (
              <div className={styles.Column}>
                <div>Автомобиль:</div>
                <div className={styles.Text}>{carName}</div>
              </div>
            )}
            {!!ox.length && (
              <div className={styles.Column}>
                <div>№ партии хранения:</div>
                <div className={styles.Row}>
                  {ox?.slice(0, 2).map(value => (
                    <div key={value} className={styles.Text}>
                      <MyLink href={`/personal/storage/?ox=${value}`}>{value}</MyLink>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {!service && (
            <div className={styles.GroupBtn}>
              <MyLink href={`https://${subDomain}.koleso.ru/rec/?widgetCancel=${code}`}>
                <Button id="btn-personal-services-update">Изменить запись / Отменить</Button>
              </MyLink>
            </div>
          )}
        </div>
      </div>
      {service && (
        <MyLink className={styles.Phone} href={`tel:${shopPhone}`}>
          <div className={styles.ServicePhone}>
            <span>Телефон сервиса:</span>
            <span>{<Phone className={styles.PhoneNumber} phone={shopPhone} />}</span>
          </div>
        </MyLink>
      )}
    </WrapperList>
  );
};

RecordService.Skeleton = function Skeleton() {
  return (
    <div className={styles.Wrapper}>
      <SkeletonLoading width={'100%'} height={'161px'} />
      <SkeletonLoading width={'100%'} height={'161px'} />
      <SkeletonLoading width={'100%'} height={'161px'} />
    </div>
  );
};
